/*
 * Replaces numbered pagination on the collection grid.
 * The next page is fetched through the Section Rendering API as the customer
 * nears the end of the grid, and its product cards are appended to the grid
 * that is already on screen. There is no button: the line under the grid
 * reports progress, and the hairline bar beneath it sweeps while a page is in
 * flight, then eases to the new total.
 *
 * Without JavaScript a plain link to the next page is shown instead, so the
 * grid stays crawlable and usable.
 */
class LoadMoreProducts extends HTMLElement {
  constructor() {
    super();
    this.status = this.querySelector('[data-load-more-status]');
    this.loading = false;
    this.observer = null;
  }

  connectedCallback() {
    if (!this.nextUrl || this.dataset.autoLoad !== 'true') return;

    if (!('IntersectionObserver' in window)) {
      // Nothing can trigger a fetch without the observer, so send the
      // customer to the next page the ordinary way.
      this.renderFallbackLink();
      return;
    }

    // Fetched two viewports early. The request itself takes a moment, but the
    // product images behind it take far longer, and arriving at a row of empty
    // frames reads as a broken page rather than a loading one.
    this.observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) this.loadNextPage();
      },
      { rootMargin: '1600px 0px' }
    );
    this.observer.observe(this);
  }

  disconnectedCallback() {
    if (this.observer) this.observer.disconnect();
  }

  get nextUrl() {
    return this.dataset.nextUrl || null;
  }

  async loadNextPage() {
    if (this.loading || !this.nextUrl) return;

    this.loading = true;
    this.setAttribute('loading', '');
    this.setAttribute('aria-busy', 'true');

    try {
      const url = new URL(this.nextUrl, window.location.origin);
      url.searchParams.set('section_id', this.dataset.sectionId);

      const response = await fetch(url.toString());
      if (!response.ok) throw new Error(`Request failed with status ${response.status}`);

      const nextDocument = new DOMParser().parseFromString(await response.text(), 'text/html');
      this.appendProducts(nextDocument);
      this.refreshStatus(nextDocument);
    } catch (error) {
      console.error('[load-more-products]', error);
      // Fall back to a normal page load so the customer is never stuck.
      window.location.href = this.nextUrl;
    } finally {
      this.loading = false;
      this.removeAttribute('loading');
      this.removeAttribute('aria-busy');
    }
  }

  appendProducts(nextDocument) {
    const grid = document.getElementById('product-grid');
    const incomingGrid = nextDocument.getElementById('product-grid');
    if (!grid || !incomingGrid) return;

    const fragment = document.createDocumentFragment();

    Array.from(incomingGrid.children).forEach((item) => {
      // The reveal animation is keyed to an index that restarts on every page,
      // so it is dropped here instead of replaying from the top of the grid.
      item.classList.remove('scroll-trigger', 'animate--slide-in');
      item.removeAttribute('data-cascade');
      item.style.removeProperty('--animation-order');
      fragment.appendChild(item);
    });

    // Promoted before the fragment enters the document: once an image is in the
    // page the browser has already settled how it loads, and the attribute no
    // longer has any say.
    this.promoteLeadingImages(Array.from(fragment.children));
    grid.appendChild(fragment);
  }

  // The cards arrive with loading="lazy" on every image, which leaves the first
  // rows blank while the browser decides they are close enough to fetch. Only
  // the leading cards are promoted: card-product.liquid keeps the rest lazy on
  // purpose, because eagerly loading a whole page of media crashes iOS Safari.
  promoteLeadingImages(added) {
    added.slice(0, LoadMoreProducts.LEADING_CARDS).forEach((item) => {
      const image = item.querySelector('.card__media img[src]');
      if (!image) return;

      image.setAttribute('loading', 'eager');
      image.setAttribute('fetchpriority', 'high');
    });
  }

  refreshStatus(nextDocument) {
    const incoming = nextDocument.querySelector('load-more-products');

    this.dataset.nextUrl = incoming ? incoming.dataset.nextUrl || '' : '';
    if (!this.nextUrl && this.observer) this.observer.disconnect();

    const incomingStatus = incoming ? incoming.querySelector('[data-load-more-status]') : null;
    if (this.status && incomingStatus) this.status.textContent = incomingStatus.textContent.trim();

    // The final page renders no bar; the CSS hides the live one once the status empties.
    const bar = this.querySelector('.load-more__bar');
    const incomingBar = incoming ? incoming.querySelector('.load-more__bar') : null;
    if (bar && incomingBar) bar.style.setProperty('--progress', incomingBar.style.getPropertyValue('--progress'));
  }

  renderFallbackLink() {
    const link = document.createElement('a');
    link.className = 'load-more__fallback link';
    link.href = this.nextUrl;
    link.textContent = this.dataset.fallbackText || 'Next page';
    this.appendChild(link);
  }
}

// Two rows on desktop, four on mobile.
LoadMoreProducts.LEADING_CARDS = 8;

customElements.define('load-more-products', LoadMoreProducts);
